import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  getAllClubs,
  getTopClubs,
  getMostActiveClubs,
  getTrendingClubs,
  getNewClubs,
  getClubById,
  getExpandedTopMembers,
  getClubMainActivity,
  globalClubActivityFeed,
  clubsSummary,
  clubTypeLabels,
  clubTypeIcons,
  clubTypeColors,
  activityTypeConfig,
  clubScoreActions,
  clubButtonConfig,
  topMembersSummary,
  radioClubs,
  programClubs,
  hostClubs,
  premiumClubs,
  type RfwClub,
  type ClubType,
  type ClubChallenge,
  type ClubActivityItem,
  type ClubJoinStatus,
} from '@/mocks/rfwClubs';

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── Page-level Club Card ─── */
function ClubCard({ club }: { club: RfwClub }) {
  const typeConfig = clubTypeColors[club.type];
  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-background-300 transition-all group cursor-pointer">
      <div className="relative h-36 overflow-hidden">
        <img src={club.thumbnailUrl} alt={club.clubName} className="w-full h-full object-cover object-top" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-background-50/20 to-transparent"></div>
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${typeConfig.badge} ${typeConfig.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${typeConfig.dot}`}></span>
            {clubTypeLabels[club.type]}
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-50/80 backdrop-blur-sm text-[10px] font-extrabold text-foreground-950">
            <i className="ri-flashlight-fill text-accent-500 text-[10px]"></i>
            {formatNumber(club.clubScore)} pts
          </span>
        </div>
        <div className="absolute bottom-3 left-4">
          <span className="text-[10px] text-foreground-600 font-medium">{club.parentName}</span>
          {club.parentFrequency && (
            <span className="text-[10px] text-foreground-400 ml-1.5">{club.parentFrequency}</span>
          )}
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-extrabold text-foreground-950 mb-1 group-hover:text-accent-600 transition-colors">{club.clubName}</h3>
        <p className="text-[11px] text-foreground-400 italic mb-3">{club.slogan}</p>
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">{club.description}</p>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatNumber(club.membersCount)}</span>
            <span className="text-[9px] text-foreground-400">Miembros</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatNumber(club.postsCount)}</span>
            <span className="text-[9px] text-foreground-400">Posts</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{club.activeChallengeCount}</span>
            <span className="text-[9px] text-foreground-400">Retos</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          {club.hasBadges && <span className="px-2 py-0.5 bg-violet-50 text-violet-600 rounded-md text-[10px] font-semibold"><i className="ri-award-line mr-0.5"></i>Insignias</span>}
          {club.hasEarlyAccess && <span className="px-2 py-0.5 bg-rose-50 text-rose-600 rounded-md text-[10px] font-semibold"><i className="ri-timer-flash-line mr-0.5"></i>Acceso anticipado</span>}
          {club.hasGiveaways && <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md text-[10px] font-semibold"><i className="ri-gift-line mr-0.5"></i>Sorteos</span>}
        </div>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-foreground-500">Engagement</span>
            <span className="text-[10px] font-bold text-foreground-700">{club.engagementRate}%</span>
          </div>
          <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div className="h-full bg-accent-500 rounded-full transition-all duration-700" style={{ width: `${club.engagementRate}%` }}></div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-[11px] font-semibold ${club.scoreChange > 0 ? 'text-emerald-600' : club.scoreChange < 0 ? 'text-red-500' : 'text-foreground-400'}`}>
            {club.scoreChange > 0 ? '+' : ''}{club.scoreChange} pts
          </span>
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-[11px] font-bold transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-user-add-line text-[10px]"></i>
            Unirme al Club
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Detail Modal ─── */
function ClubDetailModal({ club, onClose }: { club: RfwClub; onClose: () => void }) {
  const [modalTab, setModalTab] = useState<'info' | 'actividad' | 'miembros' | 'score'>('info');
  const [joinStatus, setJoinStatus] = useState<ClubJoinStatus>('none');
  const typeConfig = clubTypeColors[club.type];
  const activity = useMemo(() => getClubMainActivity(club.id, 8), [club.id]);
  const topMembers = useMemo(() => getExpandedTopMembers(club.id, 10), [club.id]);

  const handleJoin = () => {
    const next = clubButtonConfig[joinStatus].nextStatus;
    setJoinStatus(next);
  };

  const btn = clubButtonConfig[joinStatus];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative h-48 overflow-hidden rounded-t-2xl">
          <img src={club.thumbnailUrl} alt={club.clubName} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent"></div>
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-background-50/80 hover:bg-background-50 text-foreground-700 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm">
            <i className="ri-close-line"></i>
          </button>
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${typeConfig.badge} ${typeConfig.text}`}>
                {clubTypeLabels[club.type]}
              </span>
              {club.scoreChange > 0 && <span className="text-[10px] font-bold text-emerald-600">▲ +{club.scoreChange} pts</span>}
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">{club.clubName}</h2>
            <p className="text-sm text-foreground-500">{club.parentName} {club.parentFrequency || ''}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 pb-6">
          <p className="text-sm text-foreground-600 leading-relaxed mb-1">{club.description}</p>
          <p className="text-xs text-foreground-400 italic mb-4">"{club.slogan}"</p>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <button
              onClick={handleJoin}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                joinStatus === 'member'
                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                  : joinStatus === 'pending'
                    ? 'bg-amber-50 text-amber-600'
                    : 'bg-accent-500 text-background-50 hover:bg-accent-600'
              }`}
            >
              <i className={`${btn.icon} text-[11px]`}></i>
              {btn.label}
            </button>
            {joinStatus === 'member' && (
              <>
                <button className="flex items-center gap-1.5 px-4 py-2 bg-violet-50 text-violet-600 hover:bg-violet-100 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-user-heart-line text-[11px]"></i>
                  Invitar Amigos
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line text-[11px]"></i>
                  Compartir Club
                </button>
              </>
            )}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2 mb-5">
            {[
              { label: 'Miembros', value: formatNumber(club.membersCount), icon: 'ri-user-line', color: 'text-accent-600' },
              { label: 'Posts', value: formatNumber(club.postsCount), icon: 'ri-article-line', color: 'text-violet-600' },
              { label: 'Retos', value: club.challengesCount, icon: 'ri-sword-line', color: 'text-orange-600' },
              { label: 'Eventos', value: club.eventsCount, icon: 'ri-calendar-event-line', color: 'text-rose-600' },
            ].map((s) => (
              <div key={s.label} className="bg-background-100 rounded-xl p-2.5 text-center">
                <i className={`${s.icon} ${s.color} text-sm mb-1 block`}></i>
                <span className="block text-base font-extrabold text-foreground-950">{s.value}</span>
                <span className="text-[9px] text-foreground-400">{s.label}</span>
              </div>
            ))}
          </div>

          {/* Modal Tabs */}
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 overflow-x-auto">
            {([
              { id: 'info' as const, label: 'Info', icon: 'ri-information-line' },
              { id: 'actividad' as const, label: 'Actividad', icon: 'ri-pulse-line' },
              { id: 'miembros' as const, label: 'Top Miembros', icon: 'ri-user-star-line' },
              { id: 'score' as const, label: 'Club Score', icon: 'ri-flashlight-line' },
            ]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setModalTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  modalTab === tab.id ? 'bg-accent-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab: Info */}
          {modalTab === 'info' && (
            <div className="space-y-4">
              {/* Club Score */}
              <div className="bg-background-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-foreground-700">RFW Club Score</span>
                  <span className="text-sm font-extrabold text-foreground-950">{club.clubScore.toLocaleString()} pts</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-foreground-400">Ranking #{club.rankingPosition}</span>
                  <span className="text-foreground-400">Engagement {club.engagementRate}%</span>
                </div>
              </div>

              {/* Benefits */}
              <h4 className="text-sm font-extrabold text-foreground-950">Beneficios</h4>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { enabled: club.hasBadges, icon: 'ri-award-line', label: 'Insignias', color: 'bg-violet-100 text-violet-700' },
                  { enabled: club.hasEarlyAccess, icon: 'ri-timer-flash-line', label: 'Acceso Anticipado', color: 'bg-rose-100 text-rose-700' },
                  { enabled: club.hasGiveaways, icon: 'ri-gift-line', label: 'Sorteos', color: 'bg-emerald-100 text-emerald-700' },
                  { enabled: club.hasRecognition, icon: 'ri-star-line', label: 'Reconocimiento', color: 'bg-amber-100 text-amber-700' },
                ].map((b) => (
                  <div key={b.label} className={`rounded-xl p-3 ${b.enabled ? b.color : 'bg-background-200 text-foreground-300'}`}>
                    <div className="flex items-center gap-1.5"><i className={`${b.icon} text-sm`}></i><span className="text-xs font-bold">{b.label}</span></div>
                    <span className="text-[10px] opacity-70">{b.enabled ? 'Disponible' : 'No disponible'}</span>
                  </div>
                ))}
              </div>

              {/* Active Challenges */}
              {club.challenges.filter(c => c.active).length > 0 && (
                <div>
                  <h4 className="text-sm font-extrabold text-foreground-950 mb-2">Retos Activos</h4>
                  <div className="space-y-2">
                    {club.challenges.filter(c => c.active).map((ch) => (
                      <div key={ch.id} className="bg-background-100 rounded-xl p-3">
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-sm font-bold text-foreground-950">{ch.title}</span>
                          <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full whitespace-nowrap">{ch.reward}</span>
                        </div>
                        <p className="text-xs text-foreground-500 mb-2">{ch.description}</p>
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-foreground-400">{ch.participants} participantes</span>
                          <span className="text-foreground-400">Finaliza: {new Date(ch.endDate).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Founded + cross-link */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-foreground-400">Fundado: {new Date(club.foundedDate).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                <Link to={`/radio/${club.parentSlug}`} onClick={onClose} className="text-[11px] font-medium text-accent-600 hover:text-accent-700 flex items-center gap-0.5 cursor-pointer whitespace-nowrap">
                  Ver {club.parentName} <i className="ri-arrow-right-line text-[10px]"></i>
                </Link>
              </div>
            </div>
          )}

          {/* Tab: Actividad */}
          {modalTab === 'actividad' && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {activity.length > 0 ? activity.map((item) => {
                const cfg = activityTypeConfig[item.type];
                return (
                  <div key={item.id} className="flex items-start gap-3 p-3 rounded-xl bg-background-100">
                    <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center shrink-0`}>
                      <i className={`${cfg.icon} ${cfg.color} text-xs`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-semibold text-foreground-950">{item.user.name}</span>
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <p className="text-[11px] text-foreground-600 leading-relaxed">{item.content}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-foreground-400">{item.timestamp}</span>
                        <span className="text-[9px] font-bold text-emerald-600">+{item.points} pts</span>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="text-center py-8 text-foreground-400">
                  <i className="ri-pulse-line text-2xl mb-2 block"></i>
                  <p className="text-xs">Actividad del club aparecerá aquí</p>
                </div>
              )}
            </div>
          )}

          {/* Tab: Miembros */}
          {modalTab === 'miembros' && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-foreground-700">Top 10 Miembros</span>
                <span className="text-[9px] text-foreground-400">· Avg Score: {formatNumber(topMembersSummary.top10AvgScore)} pts</span>
              </div>
              <div className="space-y-2">
                {topMembers.map((m, i) => (
                  <div key={m.id} className="flex items-center gap-3 bg-background-100 rounded-xl p-3">
                    <div className="shrink-0">
                      {i === 0 ? <span className="text-base">🥇</span>
                       : i === 1 ? <span className="text-base">🥈</span>
                       : i === 2 ? <span className="text-base">🥉</span>
                       : <span className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center text-[10px] font-extrabold text-foreground-400">{i + 1}</span>
                      }
                    </div>
                    <img src={m.avatarUrl} alt={m.name} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground-950 truncate">{m.name}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${
                          m.role === 'fundador' ? 'bg-amber-100 text-amber-700'
                          : m.role === 'moderador' ? 'bg-sky-100 text-sky-700'
                          : m.role === 'top_contribuidor' ? 'bg-violet-100 text-violet-700'
                          : 'bg-background-200 text-foreground-500'
                        }`}>
                          {m.role === 'fundador' ? 'Fundador' : m.role === 'moderador' ? 'Moderador' : m.role === 'top_contribuidor' ? 'Top Contribuidor' : 'Miembro'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-sm font-extrabold text-foreground-950">{formatNumber(m.score)}</span>
                      <span className="block text-[9px] text-foreground-400">pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab: Score */}
          {modalTab === 'score' && (
            <div>
              <p className="text-xs text-foreground-500 leading-relaxed mb-4">
                El Club Score mide la salud y vitalidad de cada club. Suma puntos cada vez que interactuás con tu comunidad.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {clubScoreActions.map((action) => (
                  <div key={action.key} className={`flex items-center gap-3 p-3 rounded-xl ${action.bg}`}>
                    <i className={`${action.icon} ${action.color} text-lg`}></i>
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{action.label}</p>
                      <p className="text-[10px] text-foreground-500">{action.desc}</p>
                      <p className="text-xs font-extrabold text-emerald-600 mt-1">+{action.points} pts</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── PAGE ─── */
export default function ClubesPage() {
  const [activeTab, setActiveTab] = useState<ClubType | 'todos'>('todos');
  const [sortBy, setSortBy] = useState<'score' | 'members' | 'engagement' | 'rising'>('score');
  const [selectedClub, setSelectedClub] = useState<RfwClub | null>(null);

  const filteredClubs = useMemo(() => {
    let clubs = activeTab === 'todos' ? getAllClubs() : getAllClubs().filter(c => c.type === activeTab);
    switch (sortBy) {
      case 'score': return [...clubs].sort((a, b) => b.clubScore - a.clubScore);
      case 'members': return [...clubs].sort((a, b) => b.membersCount - a.membersCount);
      case 'engagement': return [...clubs].sort((a, b) => b.engagementRate - a.engagementRate);
      case 'rising': return [...clubs].sort((a, b) => b.scoreChange - a.scoreChange);
      default: return clubs;
    }
  }, [activeTab, sortBy]);

  const topClubs = useMemo(() => getTopClubs(3), []);
  const trendingClubs = useMemo(() => getTrendingClubs(5), []);

  const tabCounts = {
    todos: clubsSummary.totalClubs,
    oficial_radio: radioClubs.length,
    oficial_programa: programClubs.length,
    oficial_locutor: hostClubs.length,
    vip: 15,
    premium: premiumClubs.length,
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">

          {/* HERO */}
          <div className="bg-background-50 rounded-2xl overflow-hidden">
            <div className="relative h-56 md:h-64 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Dynamic%20vibrant%20community%20club%20gathering%20atmosphere%20with%20diverse%20people%20connecting%20and%20celebrating%20together%2C%20warm%20golden%20and%20amber%20lighting%2C%20modern%20community%20space%2C%20people%20holding%20hands%20forming%20circle%2C%20inclusive%20energetic%20club%20vibe%2C%20editorial%20photography%2C%20wide%20angle%20shot%20with%20warm%20tones%20and%20joyful%20energy&width=1600&height=900&seq=clubes-hero-v2&orientation=landscape"
                alt="Clubes RFW"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center w-full">
                <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
                  <i className="ri-group-fill text-3xl text-accent-400"></i>
                </div>
                <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
                  Clubes de Fans Completos
                </h1>
                <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
                  Los Clubes son el corazón social de RFW. Publicaciones, fotos, videos, eventos, concursos, retos y rankings internos. Cada radio tiene su Club Oficial.
                </p>
                <div className="flex items-center gap-3 mt-5 flex-wrap justify-center">
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {clubsSummary.totalClubs} clubes
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {formatNumber(clubsSummary.totalMembers)}+ miembros
                  </span>
                  <span className="px-3 py-1 rounded-full bg-background-50/10 backdrop-blur-md border border-background-50/20 text-background-50 text-[11px] font-semibold">
                    {clubsSummary.totalActiveChallenges} retos activos
                  </span>
                </div>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-background-200">
              {[
                { label: 'Publicaciones', value: formatNumber(clubsSummary.totalPosts), icon: 'ri-article-line', color: 'text-primary-600' },
                { label: 'Retos', value: clubsSummary.totalChallenges.toString(), icon: 'ri-sword-line', color: 'text-orange-600' },
                { label: 'Eventos', value: clubsSummary.totalEvents.toString(), icon: 'ri-calendar-event-line', color: 'text-rose-600' },
                { label: 'Top Score', value: formatNumber(clubsSummary.topClubScore), icon: 'ri-flashlight-fill', color: 'text-accent-600' },
              ].map((stat) => (
                <div key={stat.label} className="px-3 py-4 text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} ${stat.color} text-sm`}></i>
                    <span className="text-[10px] text-foreground-500">{stat.label}</span>
                  </div>
                  <div className="text-lg font-extrabold text-foreground-950">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* FILTER TABS */}
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto flex-wrap">
              {([
                { key: 'todos' as const, label: 'Todos', icon: 'ri-group-line' },
                { key: 'oficial_radio' as const, label: 'Radios', icon: 'ri-radio-line' },
                { key: 'oficial_programa' as const, label: 'Programas', icon: 'ri-calendar-check-line' },
                { key: 'oficial_locutor' as const, label: 'Locutores', icon: 'ri-mic-line' },
                { key: 'vip' as const, label: 'VIP', icon: 'ri-vip-crown-line' },
                { key: 'premium' as const, label: 'Premium', icon: 'ri-vip-diamond-line' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'bg-accent-500 text-background-50 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-background-50/20' : 'bg-background-200'}`}>
                    {tabCounts[tab.key]}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-foreground-400 font-semibold">Ordenar:</span>
              {([
                { key: 'score' as const, label: 'Score' },
                { key: 'members' as const, label: 'Miembros' },
                { key: 'engagement' as const, label: 'Engagement' },
                { key: 'rising' as const, label: 'Creciendo' },
              ]).map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === s.key ? 'bg-secondary-500 text-background-50 dark:text-foreground-950' : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* FEATURED — Top 3 */}
          {activeTab === 'todos' && (
            <div className="mt-6 bg-background-100 rounded-3xl p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-fire-line text-base text-accent-600"></i>
                </div>
                <div>
                  <h2 className="text-lg font-extrabold text-foreground-950">Clubes Destacados</h2>
                  <p className="text-xs text-foreground-400">Los mejores por RFW Club Score</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {topClubs.map((club, i) => (
                  <div key={club.id} className="bg-background-50 rounded-2xl p-5 cursor-pointer hover:-translate-y-1 transition-all" onClick={() => setSelectedClub(club)}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                        <span className="text-xl">{['🥇', '🥈', '🥉'][i]}</span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-extrabold text-foreground-950 truncate">{club.clubName}</h3>
                        <p className="text-[10px] text-foreground-400">{club.parentName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-foreground-500">{formatNumber(club.membersCount)} miembros</span>
                      <span className="text-foreground-300">·</span>
                      <span className="font-extrabold text-accent-600">{formatNumber(club.clubScore)} pts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TRENDING STRIP */}
          {activeTab === 'todos' && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-rose-100 flex items-center justify-center">
                  <i className="ri-fire-line text-xs text-rose-600"></i>
                </div>
                <h3 className="text-sm font-extrabold text-foreground-950">Clubes en Tendencia</h3>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {trendingClubs.map((club) => {
                  const tc = clubTypeColors[club.type];
                  return (
                    <div key={club.id} className="shrink-0 w-56 bg-background-50 rounded-2xl p-4 border border-background-200/70 hover:border-background-300 cursor-pointer transition-all" onClick={() => setSelectedClub(club)}>
                      <div className="flex items-center gap-2 mb-2">
                        <img src={club.parentLogoUrl} alt={club.clubName} className="w-10 h-10 rounded-xl object-cover" loading="lazy" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-foreground-950 truncate">{club.clubName}</p>
                          <span className={`inline-flex items-center gap-0.5 text-[9px] font-medium ${tc.text}`}>
                            <span className={`w-1 h-1 rounded-full ${tc.dot}`}></span>
                            {clubTypeLabels[club.type]}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-foreground-500">{club.engagementRate}% engagement</span>
                        <span className={`font-bold ${club.scoreChange > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {club.scoreChange > 0 ? '+' : ''}{club.scoreChange}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CLUBS GRID */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClubs.map((club) => (
              <div key={club.id} onClick={() => setSelectedClub(club)}>
                <ClubCard club={club} />
              </div>
            ))}
          </div>

          {/* CLUB SCORE EXPLANATION */}
          <div className="mt-10 bg-background-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center">
                <i className="ri-flashlight-fill text-base text-secondary-600"></i>
              </div>
              <h2 className="text-xl font-extrabold text-foreground-950">¿Cómo se Calcula el Club Score?</h2>
            </div>
            <p className="text-sm text-foreground-500 leading-relaxed mb-6">
              El RFW Club Score mide la salud y vitalidad de cada club. No se trata solo de cuántos miembros tiene, sino de qué tan activa y comprometida es la comunidad.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: 'ri-group-line', title: 'Miembros', desc: 'Nuevos miembros (+10), actividad de miembros (+1), compartidos del club (+5).' },
                { icon: 'ri-article-line', title: 'Actividad', desc: 'Publicaciones, comentarios, likes. Cada interacción dentro del club suma puntos.' },
                { icon: 'ri-sword-line', title: 'Retos', desc: 'Participación en retos, desafíos internos y competencias entre clubes.' },
                { icon: 'ri-calendar-event-line', title: 'Eventos', desc: 'Eventos del club (+10), concursos del club (+15), victorias en competencia (+50).' },
                { icon: 'ri-share-forward-line', title: 'Compartidos', desc: 'Cuando miembros comparten contenido del club en otras plataformas (+5).' },
                { icon: 'ri-vip-diamond-line', title: 'Premios', desc: 'Clubes que ganan competencias reciben bonos importantes de Score (+50).' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-5">
                  <div className="w-9 h-9 rounded-xl bg-background-200 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-sm text-foreground-600`}></i>
                  </div>
                  <h4 className="text-sm font-extrabold text-foreground-950 mb-1.5">{item.title}</h4>
                  <p className="text-[12px] text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CROSS LINKS */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Link to="/rankings" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-bar-chart-2-line text-primary-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Rankings RFW</h4>
                  <p className="text-xs text-foreground-500">Ranking de clubes</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/club-vip" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-vip-crown-line text-amber-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Club VIP</h4>
                  <p className="text-xs text-foreground-500">Membresías premium</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/gamificacion" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-accent-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Gamificación</h4>
                  <p className="text-xs text-foreground-500">Sistema de logros</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
            <Link to="/embajadores" className="bg-background-50 rounded-2xl p-5 hover:bg-background-100 transition-colors cursor-pointer group border border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <i className="ri-user-heart-line text-emerald-600"></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground-950">Embajadores</h4>
                  <p className="text-xs text-foreground-500">Promotores oficiales</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-600 transition-colors"></i>
              </div>
            </Link>
          </div>

          {/* Regla Final */}
          <div className="mt-10 p-6 rounded-2xl bg-accent-100/70 border border-accent-200/50 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-3">
              <i className="ri-radio-line text-lg text-background-50"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">
              Cada Radio debe poder tener su Club Oficial.
            </p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              El Club Oficial de cada radio es el espacio donde los oyentes se convierten en comunidad. Publicaciones, fotos, videos, eventos, concursos, ranking interno y moderadores. RFW garantiza que cada frecuencia — sin importar su tamaño — tenga las herramientas para construir su comunidad.
            </p>
          </div>

        </div>
      </main>

      <Footer />

      <div className="md:hidden h-20"></div>

      {selectedClub && (
        <ClubDetailModal club={selectedClub} onClose={() => setSelectedClub(null)} />
      )}
    </div>
  );
}